import * as React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import * as i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import { useDocuments } from '../../hooks/useDocuments';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { isRecentlyUpdated } from '../../constants/recentlyUpdated';

const VIEWABLE_EXTS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

function isViewableInBrowser(filename: string): boolean {
    const ext = (filename || '').split('.').pop()?.toLowerCase() ?? '';
    return VIEWABLE_EXTS.includes(ext);
}

function Documents() {
    const { data: allDocuments = [], isLoading } = useDocuments();

    const filteredDocuments = React.useMemo(() => {
        return [...allDocuments]
            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
            .slice(0, 5);
    }, [allDocuments]);

    return (
        <Box data-testid="documents-panel">
            <Typography variant="h5" fontWeight={600} gutterBottom>
                {i18next.t('headers.documents')}
            </Typography>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 1.5, position: 'relative', pb: 5 }}>
                {isLoading && <Typography variant="body2" color="text.secondary">{i18next.t('common.loading')}</Typography>}
                {filteredDocuments.map(doc => (
                    <Box key={doc.filename} sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {moment(doc.createdDate).format('L')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isViewableInBrowser(doc.filename) ? (
                                <Typography
                                    component={Link}
                                    to={`/documents/${doc.documentId}`}
                                    sx={{ color: 'secondary.main', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {doc.title}
                                </Typography>
                            ) : (
                                <Typography
                                    component="a"
                                    href={doc.documentUrl}
                                    target="_blank"
                                    sx={{ color: 'secondary.main', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {doc.title}
                                </Typography>
                            )}
                            {isRecentlyUpdated(doc.updateDateUTC) && (
                              <Chip size="small" label={i18next.t('site.recentlyUpdated')} data-testid="recently-updated" color="success"
                                    sx={{ height: 18, flexShrink: 0, '& .MuiChip-label': { px: 1.0, fontSize: '0.65rem' } }} />
                            )}
                        </Box>
                    </Box>
                ))}
                <Button
                    component={Link}
                    to={'/documents'}
                    size="small"
                    color="secondary"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ position: 'absolute', bottom: 8, right: 8, borderRadius: 0 }}
                >
                    {i18next.t('common.readmore')}
                </Button>
            </Paper>
        </Box>
    );
}

export default withTranslation('translations')(Documents);
